import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { isNil, head, reject, pipe, take } from 'ramda'
import { isFalsy, isEmptyString, isEmptyArray } from 'ramda-adjunct'
import { useIntl } from 'gatsby-plugin-intl'
import { useSelector } from 'react-redux'
import debounce from 'lodash.debounce'
import moment from 'moment'
import { breakpoint } from 'src/theme/grid'
import { COUNTRIES_OPTIONS } from 'src/ducks/consts'
import PlaceSuggestion from 'src/components/PlaceSuggestion'
import useClickOutside from 'src/hooks/useClickOutside'
import { selectCurrentUserId } from 'src/features/account/duck/selectors'
import {
  getLastUsedStorageKey,
  readLastUsedArray,
  writeLastUsedArray
} from './lastUsedStorage'
import {
  FilterBox,
  FilterDropdown,
  SearchInput,
  SearchInputWrapper,
  SelectOptionsWrapper,
  SelectRow,
  OptionsSectionTitle
} from './components/atoms'
import DatesRange from './components/DatesRange'
import Tabs, { TabContent } from './components/Tabs'
import ReactDOM from 'react-dom'
import CircularProgress from '@material-ui/core/CircularProgress'

const PlaceAndDatesRange = ({
  className,
  id,
  label,
  locationName,
  locationValue,
  locationCountryName,
  locationCountryValue,
  locationTabName,
  dateFromName,
  dateFromValue,
  dateFromLabel,
  dateToName,
  dateToValue,
  dateToLabel,
  datesTabName,
  placeholder,
  onChange,
  onChangeBulk,
  locationTypes,
  dictionary,
  isLoading,
  fetchLocations,
  searchHintText,
  initialTab = 1
}) => {
  const intl = useIntl()
  const currentUserId = useSelector(selectCurrentUserId)
  const [filterOpened, setFilterOpened] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [lastUsedOptions, setLastUsedOptions] = useState([])
  const [lastUsedCountries, setLastUsedCountries] = useState([])
  const [showSuggestionModal, setShowSuggestionModal] = useState(false)
  const [showSuggestionButton, setShowSuggestionButton] = useState(false)
  const toggleDropdown = () => setFilterOpened(!filterOpened)
  const componentRef = useClickOutside(() => setFilterOpened(false))
  const emptyText = isEmptyString(searchText) || searchText.length < 2
  const lastUsedLocalStorageKey = getLastUsedStorageKey(
    dictionary.name,
    locationTypes,
    currentUserId
  )
  const lastUsedCountriesKey = getLastUsedStorageKey(
    'countries',
    locationTypes,
    currentUserId
  )

  const debouncedSearch = useCallback(
    debounce(
      text =>
        fetchLocations({
          filter: {
            type: locationTypes,
            name: text
          },
          dictionaryName: dictionary.name,
          limit: dictionary.limit,
          page: dictionary.page
        }),
      200
    ),
    []
  )

  useEffect(() => {
    if (emptyText) return
    debouncedSearch(searchText)
  }, [searchText])

  useEffect(() => {
    setLastUsedOptions(readLastUsedArray(lastUsedLocalStorageKey))
    setLastUsedCountries(readLastUsedArray(lastUsedCountriesKey))
  }, [lastUsedLocalStorageKey, lastUsedCountriesKey])

  const saveLastUsedElement = option => {
    const optionsToSave = [option.toJS()].concat(take(4)(lastUsedOptions))
    writeLastUsedArray(lastUsedLocalStorageKey, optionsToSave)
    setLastUsedOptions(optionsToSave)
  }

  const saveLastUsedCountry = country => {
    const existingCountries = lastUsedCountries.filter(
      c => c.value !== country.value
    )

    const countriesToSave = [country].concat(take(4)(existingCountries))
    writeLastUsedArray(lastUsedCountriesKey, countriesToSave)
    setLastUsedCountries(countriesToSave)
  }

  const isMultiple = () => Array.isArray(locationValue)

  const hasValue = () => {
    const selectedDates = pipe(reject(isNil))([dateFromValue, dateToValue])
    const isSelectedLocationEmpty =
      isEmptyArray(locationValue) || isNil(locationValue)

    return !(isEmptyArray(selectedDates) && isSelectedLocationEmpty)
  }

  const selectedOptionValue = locationId =>
    isMultiple()
      ? head(locationValue.filter(obj => obj.id === locationId))
      : locationValue

  const generateFilterName = () => {
    const selectedLocation = isMultiple() ? head(locationValue) : locationValue
    const selectedCountries = locationCountryValue || []
    const selectedDates = pipe(reject(isNil))([dateFromValue, dateToValue])
    const formattedDates = selectedDates
      .map(date => moment(date).format('DD.MM'))
      .join('-')

    const parts = []
    if (!isEmptyArray(selectedDates)) parts.push(formattedDates)
    if (selectedLocation) parts.push(selectedLocation.name)
    if (!isEmptyArray(selectedCountries)) {
      parts.push(selectedCountries.join(', '))
    }

    return parts.length > 0 ? parts.join(', ') : label
  }

  const generateLocationTabName = () => {
    const number =
      isMultiple() && locationValue.length > 0 ? locationValue.length : null
    return `${locationTabName} ${number ? `(${number})` : ''}`
  }

  const generateCountryTabName = () => {
    const number =
      locationCountryValue && locationCountryValue.length > 0
        ? locationCountryValue.length
        : null
    return `${intl.formatMessage({ id: 'common.country' })} ${
      number ? `(${number})` : ''
    }`
  }

  const handleLocationChange = (optionValue, isThisValueSelected) => {
    const singleValue = () => (isThisValueSelected ? null : optionValue)
    const multipleValue = () =>
      isThisValueSelected
        ? locationValue.filter(obj => obj.id !== optionValue.id)
        : [...locationValue, optionValue]
    onChange(locationName, isMultiple() ? multipleValue() : singleValue())
  }

  const handleLocationRangeChange = (range, optionValue, isSelected) => {
    const singleValue = () => ({ ...optionValue, range })
    const multipleValue = () =>
      isSelected
        ? locationValue.map(obj =>
            obj.id === optionValue.id ? { ...obj, range } : obj
          )
        : [...locationValue, { ...optionValue, range }]

    onChange(locationName, isMultiple() ? multipleValue() : singleValue())
  }

  const handleClear = e => {
    !isFalsy(e) && e.stopPropagation()
    setSearchText('')
    onChangeBulk({
      type: 'bulk',
      [locationName]: isMultiple() ? [] : null,
      [locationCountryName]: [],
      [dateFromName]: null,
      [dateToName]: null
    })
  }

  const handleCountryChange = (value, isSelected) => {
    const currentValues = locationCountryValue || []
    let newValue

    if (isSelected) {
      newValue = currentValues.filter(v => v !== value.value)
    } else {
      newValue = [...currentValues, value.value]
      saveLastUsedCountry(value)
    }

    onChange(locationName + 'Country', newValue)
  }

  return (
    <div className={className} ref={componentRef}>
      <FilterBoxStyled
        onClick={toggleDropdown}
        opened={filterOpened}
        hasValue={hasValue()}
        id={`filter-${id}`}
        selectedNumber={isMultiple() ? locationValue.length : null}
        onClear={handleClear}
      >
        {generateFilterName()}
      </FilterBoxStyled>
      {filterOpened && (
        <FilterDropdown
          filterName={label}
          onClose={() => setFilterOpened(false)}
          onClear={handleClear}
          clearButtonText={intl.formatMessage({ id: 'offersFilters.clear' })}
          confirmButtonText={intl.formatMessage({
            id: 'offersFilters.confirm'
          })}
        >
          <Tabs
            list={[
              generateCountryTabName(),
              generateLocationTabName(),
              datesTabName
            ]}
            initialTab={initialTab}
          >
            <TabContent>
              <SelectOptionsWrapperStyled>
                {!isEmptyArray(lastUsedCountries) && (
                  <>
                    <OptionsSectionTitle>
                      {intl.formatMessage({ id: 'offersFilters.lastOptions' })}
                    </OptionsSectionTitle>
                    {lastUsedCountries.map(option => {
                      const currentValues = locationCountryValue || []
                      const isSelected = currentValues.includes(option.value)

                      return (
                        <SelectRow
                          key={`last-${option.value}`}
                          selected={isSelected}
                          name={intl.formatMessage({
                            id: `countries.${option.value}`
                          })}
                          onClick={() =>
                            handleCountryChange(option, isSelected)
                          }
                        />
                      )
                    })}
                    <OptionsSectionTitle>
                      {intl.formatMessage({ id: 'offersFilters.allOptions' })}
                    </OptionsSectionTitle>
                  </>
                )}

                {COUNTRIES_OPTIONS.map(option => {
                  const currentValues = locationCountryValue || []
                  const isSelected = currentValues.includes(option.value)

                  return (
                    <SelectRow
                      key={option.value}
                      selected={isSelected}
                      name={intl.formatMessage({
                        id: `countries.${option.value}`
                      })}
                      onClick={() => handleCountryChange(option, isSelected)}
                    />
                  )
                })}
              </SelectOptionsWrapperStyled>
            </TabContent>
            <TabContent>
              <SearchInputWrapper>
                <SearchInput
                  name={locationName}
                  id={`input-${id}`}
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder={placeholder}
                />
              </SearchInputWrapper>
              <SelectOptionsWrapperStyled>
                {emptyText && isEmptyArray(lastUsedOptions) && (
                  <SearchHint>{searchHintText}</SearchHint>
                )}
                {!emptyText &&
                  isEmptyArray(dictionary.values) &&
                  (isLoading ? (
                    <>
                      <LoaderContainer>
                        <Loader />
                      </LoaderContainer>
                      {(() => {
                        setTimeout(() => {
                          setShowSuggestionButton(true)
                        }, 0)
                        return null
                      })()}
                    </>
                  ) : (
                    (!dictionary ||
                      !dictionary.values ||
                      isEmptyArray(dictionary.values)) &&
                    !isLoading &&
                    showSuggestionButton && (
                      <>
                        <SearchHint>
                          {intl.formatMessage({
                            id: 'offersFilters.placeNotFound'
                          })}
                        </SearchHint>
                        <SuggestButton
                          onClick={() => setShowSuggestionModal(true)}
                        >
                          {intl.formatMessage({ id: 'common.addSuggestion' })}
                        </SuggestButton>
                      </>
                    )
                  ))}
                {emptyText && !isEmptyArray(locationValue) && (
                  <>
                    <OptionsSectionTitle>
                      {intl.formatMessage({
                        id: 'offersFilters.chosenOptions'
                      })}
                    </OptionsSectionTitle>
                    {locationValue.map((option, key) => {
                      const selectedOptionData = selectedOptionValue(option.id)
                      const isSelected = !isFalsy(selectedOptionData)
                      return (
                        <SelectRow
                          key={key}
                          selected={isSelected}
                          name={option.name}
                          stepper
                          onRangeChange={range =>
                            handleLocationRangeChange(range, option, isSelected)
                          }
                          range={
                            !isFalsy(selectedOptionData)
                              ? selectedOptionData.range
                              : 0
                          }
                          onClick={() => {
                            handleLocationChange(option, isSelected)
                          }}
                        />
                      )
                    })}
                  </>
                )}
                {emptyText && !isEmptyArray(lastUsedOptions) && (
                  <>
                    <OptionsSectionTitle>
                      {intl.formatMessage({ id: 'offersFilters.lastOptions' })}
                    </OptionsSectionTitle>
                    {lastUsedOptions.map((option, key) => {
                      const selectedOptionData = selectedOptionValue(
                        option.value
                      )
                      const isSelected = !isFalsy(selectedOptionData)
                      return (
                        <SelectRow
                          key={key}
                          selected={isSelected}
                          name={option.label}
                          stepper
                          onRangeChange={range =>
                            handleLocationRangeChange(
                              range,
                              { id: option.value, name: option.label },
                              isSelected
                            )
                          }
                          range={
                            !isFalsy(selectedOptionData)
                              ? selectedOptionData.range
                              : 0
                          }
                          onClick={() => {
                            handleLocationChange(
                              {
                                id: option.value,
                                range: 0,
                                name: option.label
                              },
                              isSelected
                            )
                          }}
                        />
                      )
                    })}
                  </>
                )}
                {!emptyText &&
                  dictionary.values.map((option, key) => {
                    const selectedOptionData = selectedOptionValue(option.value)
                    const isSelected = !isFalsy(selectedOptionData)
                    return (
                      <SelectRow
                        key={key}
                        selected={isSelected}
                        name={option.label}
                        stepper
                        onRangeChange={range =>
                          handleLocationRangeChange(
                            range,
                            { id: option.value, name: option.label },
                            isSelected
                          )
                        }
                        range={
                          !isFalsy(selectedOptionData)
                            ? selectedOptionData.range
                            : 0
                        }
                        onClick={() => {
                          handleLocationChange(
                            { id: option.value, range: 0, name: option.label },
                            isSelected
                          )
                          !isSelected && saveLastUsedElement(option)
                        }}
                      />
                    )
                  })}
              </SelectOptionsWrapperStyled>
            </TabContent>
            <TabContent>
              <DatesRange
                dateFromName={dateFromName}
                dateFromValue={dateFromValue}
                dateFromLabel={dateFromLabel}
                dateToName={dateToName}
                dateToValue={dateToValue}
                dateToLabel={dateToLabel}
                onChange={onChange}
              />
            </TabContent>
          </Tabs>
        </FilterDropdown>
      )}
      {showSuggestionModal &&
        ReactDOM.createPortal(
          <PlaceSuggestion
            onClose={() => setShowSuggestionModal(false)}
            placeName={searchText}
            locationType={locationTypes}
          />,
          document.body
        )}
    </div>
  )
}

PlaceAndDatesRange.defaultProps = {
  value: '',
  locationCountryValue: [],
  initialTab: 1
}

export default styled(PlaceAndDatesRange)`
  display: flex;
  position: relative;
  ${FilterDropdown} {
    ${breakpoint.m`
      width: 400px;
      height: 530px;
    `}
  }
`
const SelectOptionsWrapperStyled = styled(SelectOptionsWrapper)`
  padding-bottom: 50px;
`

const FilterBoxStyled = styled(FilterBox)`
  width: 100%;
  ${breakpoint.m`
    width: 180px;
  `}
`

const SearchHint = styled.span`
  display: flex;
  padding: 0.8rem;
  color: ${({ theme }) => theme.colors.black.alpha(0.7).hex()};
  font-weight: 400;
  font-size: 0.875rem;
  line-height: 1.71;
`

const SuggestButton = styled.button`
  display: flex;
  addcommentMore actions
  align-items: center;
  justify-content: center;
  padding: 0.8rem;
  background-color: ${({ theme }) => theme.colors.apple.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.71;
  border: none;
  cursor: pointer;
  margin: 0.5rem auto;
  width: fit-content;
`
const Loader = styled(CircularProgress)`
  width: 25px !important;
  height: 25px !important;
  color: ${({ theme }) => theme.colors.apple.hex()} !important;
  transform-origin: center center;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
  width: 100%;
  min-height: 100px;
  background: transparent;
  position: static;
  box-sizing: border-box;
  overflow: hidden;
`
